import React from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useQueryClient } from '@tanstack/react-query';
import { useSyncFavorites, favoritesKeys } from '@/hooks/favorites/useFavorites';

interface FavoritesStore {
  favoriteIds: Set<string>;
  userRatings: Record<string, number>;
  isOnline: boolean;
  lastSyncAt: number | null;
  pendingChanges: Set<string>; // Track changes when offline
  
  // Actions - now hybrid (localStorage + API)
  toggleFavorite: (recipeId: string) => void;
  isFavorite: (recipeId: string) => boolean;
  setRating: (recipeId: string, rating: number) => void;
  getRating: (recipeId: string) => number;
  clearFavorites: () => void;
  
  // New hybrid actions
  setOnlineStatus: (isOnline: boolean) => void;
  syncWithServer: () => Promise<void>;
  updateFromServer: (serverFavoriteIds: string[]) => void;
  addPendingChange: (recipeId: string) => void;
  clearPendingChanges: () => void;
  
  // Batch operations
  setFavorites: (favoriteIds: string[]) => void;
  getFavoriteIds: () => string[];
}

// Global reference to React Query client (will be set by hook)
let globalQueryClient: ReturnType<typeof useQueryClient> | null = null;

export const useFavoritesStore = create<FavoritesStore>()(
  persist(
    (set, get) => ({
      favoriteIds: new Set<string>(),
      userRatings: {},
      isOnline: navigator.onLine,
      lastSyncAt: null,
      pendingChanges: new Set<string>(),

      toggleFavorite: (recipeId: string) => {
        const state = get();
        const wasRemoving = state.favoriteIds.has(recipeId);
        
        // Update local state immediately
        set((state) => {
          const newFavoriteIds = new Set(state.favoriteIds);
          const newPendingChanges = new Set(state.pendingChanges);
          
          if (newFavoriteIds.has(recipeId)) {
            newFavoriteIds.delete(recipeId);
          } else {
            newFavoriteIds.add(recipeId);
          }
          
          // Track pending change if offline
          if (!state.isOnline) {
            newPendingChanges.add(recipeId);
          }
          
          return { 
            favoriteIds: newFavoriteIds,
            pendingChanges: newPendingChanges
          };
        });

        // If online, trigger API call via React Query hook
        // Note: This will be handled by the component using the hook
        if (state.isOnline && globalQueryClient) {
          // The actual API call will be made by the component using useToggleFavorite
          // We just update the React Query cache to keep it in sync
          globalQueryClient.setQueryData(
            favoritesKeys.status(recipeId),
            !wasRemoving
          );
        }
      },

      isFavorite: (recipeId: string) => {
        return get().favoriteIds.has(recipeId);
      },

      setRating: (recipeId: string, rating: number) => {
        set((state) => ({
          userRatings: {
            ...state.userRatings,
            [recipeId]: rating
          }
        }));
      },

      getRating: (recipeId: string) => {
        return get().userRatings[recipeId] || 0;
      },

      clearFavorites: () => {
        set({ 
          favoriteIds: new Set(), 
          userRatings: {},
          pendingChanges: new Set()
        });
        
        // Clear React Query cache if available
        if (globalQueryClient) {
          globalQueryClient.invalidateQueries({ queryKey: favoritesKeys.all });
        }
      },

      // New hybrid actions
      setOnlineStatus: (isOnline: boolean) => {
        const wasOffline = !get().isOnline;
        set({ isOnline });
        
        // If coming back online and we have pending changes, sync
        if (isOnline && wasOffline && get().pendingChanges.size > 0) {
          // Trigger sync - this should be called by a component with access to hooks
          console.log('Back online, sync needed for:', Array.from(get().pendingChanges));
        }
      },

      syncWithServer: async () => {
        const state = get();
        if (!state.isOnline) return;

        try {
          // This should be called by a component that has access to the sync hook
          // For now, we just update the sync timestamp
          set({ 
            lastSyncAt: Date.now(),
            pendingChanges: new Set() // Clear pending changes after successful sync
          });
          
        } catch (error) {
          console.error('Failed to sync favorites:', error);
        }
      },

      updateFromServer: (serverFavoriteIds: string[]) => {
        set({ 
          favoriteIds: new Set(serverFavoriteIds),
          lastSyncAt: Date.now(),
          pendingChanges: new Set()
        });
      },

      addPendingChange: (recipeId: string) => {
        set((state) => ({
          pendingChanges: new Set(state.pendingChanges).add(recipeId)
        }));
      },

      clearPendingChanges: () => {
        set({ pendingChanges: new Set() });
      },

      // Batch operations
      setFavorites: (favoriteIds: string[]) => {
        set({ favoriteIds: new Set(favoriteIds) });
      },

      getFavoriteIds: () => {
        return Array.from(get().favoriteIds);
      }
    }),
    {
      name: 'favorites-storage', // unique name for localStorage key
      
      // Custom serialization because Set is not JSON serializable
      serialize: (state) => {
        return JSON.stringify({
          ...state.state,
          favoriteIds: Array.from(state.state.favoriteIds),
          pendingChanges: Array.from(state.state.pendingChanges)
        });
      },
      
      // Custom deserialization to recreate Set from array
      deserialize: (str) => {
        const parsed = JSON.parse(str);
        return {
          ...parsed,
          state: {
            ...parsed.state,
            favoriteIds: new Set(parsed.state.favoriteIds || []),
            pendingChanges: new Set(parsed.state.pendingChanges || []),
            isOnline: navigator.onLine, // Always use current online status
            lastSyncAt: parsed.state.lastSyncAt || null
          }
        };
      }
    }
  )
);

// Hook to connect the store with React Query
export function useFavoritesStoreSync() {
  const queryClient = useQueryClient();
  const syncFavorites = useSyncFavorites();
  const store = useFavoritesStore();

  // Set global query client reference
  globalQueryClient = queryClient;

  // Online/offline event listeners
  React.useEffect(() => {
    const handleOnline = () => {
      store.setOnlineStatus(true);
      
      // Sync pending changes if any
      if (store.pendingChanges.size > 0) {
        syncFavorites.mutate(store.getFavoriteIds(), {
          onSuccess: (result) => {
            store.updateFromServer(result.serverFavorites.map(f => f.recipeId));
            console.log('Successfully synced favorites after reconnection');
          },
          onError: (error) => {
            console.error('Failed to sync favorites after reconnection:', error);
          }
        });
      }
    };

    const handleOffline = () => {
      store.setOnlineStatus(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [store, syncFavorites]);

  // Sync with server on mount if online and haven't synced recently
  React.useEffect(() => {
    const now = Date.now();
    const lastSync = store.lastSyncAt;
    const syncInterval = 5 * 60 * 1000; // 5 minutes

    if (store.isOnline && (!lastSync || (now - lastSync) > syncInterval)) {
      syncFavorites.mutate(store.getFavoriteIds(), {
        onSuccess: (result) => {
          store.updateFromServer(result.serverFavorites.map(f => f.recipeId));
          console.log('Successfully synced favorites on mount');
        },
        onError: (error) => {
          console.error('Failed to sync favorites on mount:', error);
        }
      });
    }
  }, []); // Only run on mount

  return {
    isOnline: store.isOnline,
    pendingChanges: Array.from(store.pendingChanges),
    lastSyncAt: store.lastSyncAt,
    isSyncing: syncFavorites.isPending
  };
}

// Export the global query client setter for use in other parts of the app
export function setGlobalQueryClient(client: ReturnType<typeof useQueryClient>) {
  globalQueryClient = client;
}
